import { combineReducers } from 'redux';
import { map, reduce } from 'lodash';
import { handleActions, combineActions } from 'redux-actions';
import build from 'redux-object';

import reduxUtils from 'common/reduxUtils';
import actions from '../actions';
import { defaultPageSize } from '../tableUtils';

const {
  OFF_RAMPS_REQUEST,
  OFF_RAMPS_SUCCESS,
  OFF_RAMPS_FAILURE,
  OFF_RAMPS_TABLE_NEXT_PAGE,
  OFF_RAMPS_TABLE_PREVIOUS_PAGE,
  RESET_OFF_RAMPS_TABLE,
  RESET_OFF_RAMPS_TABLE_PAGE,
  SET_OFF_RAMPS_TABLE_FILTER,
  SET_OFF_RAMPS_TABLE_PAGE,
  SET_OFF_RAMPS_TABLE_PAGE_SIZE,
  SET_OFF_RAMPS_TABLE_SORT,
  SINGLE_OFF_RAMP_REQUEST,
  SINGLE_OFF_RAMP_SUCCESS,
  SINGLE_OFF_RAMP_FAILURE,
  CREATE_OFF_RAMP_BILL_REQUEST,
  CREATE_OFF_RAMP_BILL_SUCCESS,
  CREATE_OFF_RAMP_BILL_FAILURE,
} = actions;

const allStatusRegex = '.*';
const defaultDateRangeValue = 'all';

const transformOffRamps = (payload) => {
  const offRamps = build(payload, 'offrampBill');

  return reduce(
    offRamps,
    (result, offRamp) => ({
      ...result,
      [offRamp.id]: {
        ...offRamp,
        cryptoAmount: offRamp.cryptoAmount,
        fiatAmount: offRamp.fiatAmount,
      },
    }),
    {}
  );
};

const transformSingleOffRamp = (payload) => {
  const offRampsList = transformOffRamps(payload);
  const paymentUrl = reduxUtils.getMetaInfo(payload, 'paymentUrl');
  const offRampId = Object.keys(offRampsList)[0];

  offRampsList[offRampId].offRampUrl = paymentUrl;
  return offRampsList;
};

export const getOffRamp = (state, id) => state.offRamps.byId[id];

const byId = handleActions(
  {
    [OFF_RAMPS_SUCCESS]: (state, action) => ({
      ...transformOffRamps(action.payload),
    }),
    [SINGLE_OFF_RAMP_SUCCESS]: (state, action) => ({
      ...state,
      ...transformSingleOffRamp(action.payload),
    }),
  },
  {}
);

const isFetchingOffRampsReducer = handleActions(
  {
    [combineActions(OFF_RAMPS_REQUEST, SINGLE_OFF_RAMP_REQUEST)]: () => true,
    [combineActions(
      OFF_RAMPS_SUCCESS,
      OFF_RAMPS_FAILURE,
      SINGLE_OFF_RAMP_SUCCESS,
      SINGLE_OFF_RAMP_FAILURE
    )]: () => false,
  },
  false
);

const getTableRow = (state, id) => {
  const offRamp = getOffRamp(state, id);
  if (!offRamp) return {};

  return {
    id: offRamp.id,
    reference: offRamp.reference,
    tradedAmount: offRamp.cryptoAmount,
    convertedAmount: offRamp.fiatAmount,
    createdAt: offRamp.createdAt,
    status: offRamp.status,
  };
};

const loadOffRampsErrorReducer = handleActions(
  {
    [combineActions(
      OFF_RAMPS_REQUEST,
      OFF_RAMPS_SUCCESS,
      SINGLE_OFF_RAMP_SUCCESS
    )]: () => null,
    [combineActions(OFF_RAMPS_FAILURE, SINGLE_OFF_RAMP_FAILURE)]: (
      state,
      action
    ) => action.payload,
  },
  null
);

const defaultFilter = {
  searchValue: undefined,
  statusValue: allStatusRegex,
  dateRangeValue: defaultDateRangeValue,
};

const offRampsTableFilterReducer = handleActions(
  {
    [SET_OFF_RAMPS_TABLE_FILTER]: (state, action) => ({
      ...state,
      ...action.payload,
    }),
    [RESET_OFF_RAMPS_TABLE]: () => defaultFilter,
  },
  defaultFilter
);

const offRampsTablePageReducer = handleActions(
  {
    [SET_OFF_RAMPS_TABLE_PAGE]: (state, action) => action.payload,
    [OFF_RAMPS_TABLE_NEXT_PAGE]: (state) => state + 1,
    [OFF_RAMPS_TABLE_PREVIOUS_PAGE]: (state) => state - 1,
    [combineActions(RESET_OFF_RAMPS_TABLE, RESET_OFF_RAMPS_TABLE_PAGE)]: () =>
      1,
  },
  1
);

const defaultSort = { id: 'createdAt', sortAscending: false };

const offRampsTableSortReducer = handleActions(
  {
    [SET_OFF_RAMPS_TABLE_SORT]: (state, action) => action.payload,
    [RESET_OFF_RAMPS_TABLE]: () => defaultSort,
  },
  defaultSort
);

const offRampsTablePaginationReducer = handleActions(
  {
    [SET_OFF_RAMPS_TABLE_PAGE_SIZE]: (state, action) => ({
      ...state,
      pageSize: action.payload,
    }),
    [OFF_RAMPS_SUCCESS]: (state, action) => ({
      ...state,
      totalElements:
        reduxUtils.getMetaInfo(action.payload, 'totalElements') || 0,
    }),
  },
  { pageSize: defaultPageSize, totalElements: 0 }
);

const createOffRampBillReducer = handleActions(
  {
    [CREATE_OFF_RAMP_BILL_REQUEST]: (state) => ({
      ...state,
      loading: true,
      error: null,
    }),
    [CREATE_OFF_RAMP_BILL_SUCCESS]: (state, action) => ({
      ...state,
      loading: false,
      data: action.payload,
    }),
    [CREATE_OFF_RAMP_BILL_FAILURE]: (state, action) => ({
      ...state,
      loading: false,
      error: action.payload,
    }),
  },
  { loading: false, error: null, data: null }
);

export const getOffRampContainer = getTableRow;

const getOffRamps = (state) => {
  const {
    offRamps: { byId: offRampsById },
  } = state;

  return map(offRampsById, (offRamp, id) => getTableRow(state, id));
};

export const isFetchingOffRamps = (state) => state.offRamps.isFetchingOffRamps;
export const getLoadOffRampsError = (state) => state.offRamps.loadOffRampsError;
export const getOffRampsTablePage = (state) => state.offRamps.offRampsTablePage;
export const getOffRampsTableFilter = (state) =>
  state.offRamps.offRampsTableFilter;
export const getOffRampsTableSort = (state) => state.offRamps.offRampsTableSort;
export const getOffRampsTableTotalElements = (state) =>
  state.offRamps.offRampsTablePagination.totalElements;
export const getOffRampsTablePageSize = (state) =>
  state.offRamps.offRampsTablePagination.pageSize;

export const getOffRampsTable = (state) => {
  const {
    offRamps: {
      offRampsTablePage: page,
      offRampsTablePagination: { pageSize, totalElements },
      offRampsTableSort: sort,
      isFetchingOffRamps: isFetching,
    },
  } = state;

  return {
    isFetching,
    page,
    pageSize,
    totalElements,
    sort,
    data: getOffRamps(state),
  };
};

export const getOffRampsTableFilters = (state) => ({
  ...state.offRamps.offRampsTableFilter,
});

export const getOffRampsScope = (state) => {
  const {
    offRamps: {
      offRampsTablePage: page,
      offRampsTablePagination: { pageSize },
      offRampsTableSort: sort,
      offRampsTableFilter: {
        searchValue: search,
        statusValue: status,
        dateRangeValue: dateRange,
      },
    },
  } = state;

  return {
    dateRange,
    page,
    pageSize,
    search,
    sort,
    status,
  };
};

export const getSingleOffRamp = (state, id) => {
  const offRamp = getOffRamp(state, id);

  if (!offRamp) return null;

  return {
    ...offRamp,
    totalAmount: offRamp.fiatAmount,
    convertedAmount: offRamp.fiatAmount,
    tradedAmount: offRamp.cryptoAmount,
    offRampUrl: offRamp.offRampUrl,
  };
};

export const isFetchingSingleOffRamp = (state) =>
  state.offRamps.isFetchingOffRamps;
export const getSingleOffRampError = (state) =>
  state.offRamps.loadOffRampsError;

export default combineReducers({
  byId,
  createOffRampBill: createOffRampBillReducer,
  isFetchingOffRamps: isFetchingOffRampsReducer,
  loadOffRampsError: loadOffRampsErrorReducer,
  offRampsTablePage: offRampsTablePageReducer,
  offRampsTableFilter: offRampsTableFilterReducer,
  offRampsTableSort: offRampsTableSortReducer,
  offRampsTablePagination: offRampsTablePaginationReducer,
});
